import { InjectionToken } from '@angular/core';
import type { Observable } from 'rxjs';
import type {
  Profile,
  Experience,
  Project,
  Technology,
  Education,
  Certification,
  SocialLink,
  SiteConfig,
  SectionConfig,
  GitHubRepo,
} from '../entities';

/**
 * Admin domain port.
 *
 * Read/write contract for all admin operations. Concrete adapters live under
 * `core/infrastructure` (e.g. `HttpAdminRepository`). Consumers should
 * depend on this interface via the `ADMIN_REPOSITORY` token, never on the
 * adapter directly.
 */
export interface AdminRepository {
  // Profile
  getProfile(): Observable<Profile>;
  updateProfile(data: Partial<Profile>): Observable<Profile>;

  // Experiences
  getExperiences(): Observable<Experience[]>;
  createExperience(data: Partial<Experience>): Observable<Experience>;
  updateExperience(id: string, data: Partial<Experience>): Observable<Experience>;
  deleteExperience(id: string): Observable<void>;

  // Projects
  getProjects(): Observable<Project[]>;
  createProject(data: Partial<Project>): Observable<Project>;
  updateProject(id: string, data: Partial<Project>): Observable<Project>;
  deleteProject(id: string): Observable<void>;

  // Technologies
  getTechnologies(): Observable<Technology[]>;
  createTechnology(data: Partial<Technology>): Observable<Technology>;
  updateTechnology(id: string, data: Partial<Technology>): Observable<Technology>;
  deleteTechnology(id: string): Observable<void>;

  // Education
  getEducation(): Observable<Education[]>;
  createEducation(data: Partial<Education>): Observable<Education>;
  updateEducation(id: string, data: Partial<Education>): Observable<Education>;
  deleteEducation(id: string): Observable<void>;

  // Certifications
  getCertifications(): Observable<Certification[]>;
  createCertification(data: Partial<Certification>): Observable<Certification>;
  updateCertification(id: string, data: Partial<Certification>): Observable<Certification>;
  deleteCertification(id: string): Observable<void>;

  // Social Links
  getSocialLinks(): Observable<SocialLink[]>;
  createSocialLink(data: Partial<SocialLink>): Observable<SocialLink>;
  updateSocialLink(id: string, data: Partial<SocialLink>): Observable<SocialLink>;
  deleteSocialLink(id: string): Observable<void>;

  // Site Config
  getSiteConfig(): Observable<SiteConfig>;
  updateSiteConfig(data: Record<string, string>): Observable<SiteConfig>;

  // Sections Config
  getSections(): Observable<SectionConfig[]>;
  updateSections(data: SectionConfig[]): Observable<SectionConfig[]>;

  // GitHub
  getGithubRepos(): Observable<GitHubRepo[]>;
  syncGithub(): Observable<{ synced: number }>;
  updateGithubRepo(id: string, data: Partial<GitHubRepo>): Observable<GitHubRepo>;

  // CV Upload
  uploadCv(file: File): Observable<{ url: string }>;
}

export const ADMIN_REPOSITORY = new InjectionToken<AdminRepository>('AdminRepository');
